import { createStore } from 'devextreme-aspnet-data-nojquery';
import { saveAs } from 'file-saver';
import store from '@/store';
import printjs from 'print-js-updated/src/index';
import { findIana } from 'windows-iana';
import { DateTime } from 'luxon';
import { NUMERIC_MAX_VALUE } from './Constants';

export default {
    toast: {
        success(message) {
            window.getApp.$emit('APP_MESSAGE', message);
        },
        info(message) {
            window.getApp.$emit('APP_INFO', message);
        },
        warning(message) {
            window.getApp.$emit('APP_WARNING', message);
        },
        error(message) {
            window.getApp.$emit('APP_ERROR', message);
        },
    },
    object: {
        isEmpty: (dto) => {
            return Object.keys(dto).length === 0;
        },
        toJS(obj) {
            return obj
                ? JSON.parse(JSON.stringify(obj))
                : obj;
        },
        clone(target, source) {
            return Object.assign(target, source);
        },
        getUUID() {
            return Math.random().toString(36).substring(2) +
                (new Date()).getTime().toString(36);
        },
    },
    date: {
        now() {
            return this.toUserFormat(
                new Date(),
                store.getters['account/user'].MetricDateFormat
            );
        },
        toIsoFromUserFormat(userDate, useTime) {
            if (!userDate)
                return null;

            const user = store.getters['account/user'];

            const userFormat = user.MetricDateFormat + (useTime ? ' HH:mm:ss' : '');
            const parsedDate = DateTime.fromFormat(userDate, userFormat);
            return parsedDate.toISO();
        },
        toUserFormatFromIso(dateString, useTime = true) {
            // Get the IANA time zone identifier from the Windows time zone identifier
            const user = store.getters['account/user'];
            const ianaTimeZone = findIana(user.TimeZoneID);

            const inputDate = DateTime.fromISO(dateString, { setZone: true });
            const outputDate = inputDate.setZone(ianaTimeZone[0]);

            const format = user.MetricDateFormat + (useTime ? ' HH:mm:ss' : '');
            return outputDate.toFormat(format);
        },
        toUserFormatFromDateTimeString(dateString) {
            const dateTimeString = dateString + 'Z';
            return this.toUserFormatFromIso(dateTimeString);
        },
        toUserFormat: function (date, format) {
            // Calculate date parts and replace instances in format string accordingly
            format = format.toUpperCase().replace('DD', (date.getDate() < 10 ? '0' : '') + date.getDate()); // Pad with '0' if needed
            format = format.toUpperCase().replace('MM', (date.getMonth() < 9 ? '0' : '') + (date.getMonth() + 1)); // Months are zero-based
            format = format.toUpperCase().replace('YYYY', date.getFullYear());
            format = format.toUpperCase().replace('YY', date.getFullYear().toString().substring(2));

            return format;
        },
        IsValidDate: function(date, format) {
            const dateToValidate = DateTime.fromFormat(date, format);
            return dateToValidate.isValid;
        },
        fromUserFormat: function (userDate, format) {
            if (this.IsValidDate(userDate, format)) {
                const formatToUpperCase = format.toUpperCase();
                var datePartIndex = formatToUpperCase.indexOf('YYYY');
                var yearSubString = 4;
                if (datePartIndex < 0) {
                    datePartIndex = formatToUpperCase.indexOf('YY');
                    yearSubString = 2;
                }
                var year = parseInt(userDate.substr(datePartIndex, yearSubString));
                if (yearSubString == 2) {
                    year += 2000;
                }
                datePartIndex = formatToUpperCase.indexOf('MM');
                const month = parseInt(userDate.substr(datePartIndex, 2));
                datePartIndex = formatToUpperCase.indexOf('DD');
                const day = parseInt(userDate.substr(datePartIndex, 2));
                if (formatToUpperCase.includes('HH:MM')) {
                    const timePartIndex = formatToUpperCase.indexOf('HH:MM');
                    const timePart = userDate.substr(timePartIndex, 5).split(':');
                    return new Date(year, month - 1, day, timePart[0], timePart[1]);
                } else {
                    return new Date(year, month - 1, day);
                }
            } else {
                return null;
            }
        },
        changeFormat: function (strDate, inputFormat, outputFormat) {
            if (strDate) {
                const date = this.fromUserFormat(strDate, inputFormat);
                if (!date)
                    return strDate;

                return this.toUserFormat(date, outputFormat);
            } else {
                return strDate;
            }
        },
        addTimeZoneFromIsoFormat: function (dateString) {
            const user = store.getters['account/user'];
            const ianaTimeZone = findIana(user.TimeZoneID);

            const inputDate = DateTime.fromISO(dateString);

            const outputDate = inputDate.setZone(ianaTimeZone[0], { keepLocalTime: true });

            const formattedDate = outputDate.toFormat("yyyy-MM-dd'T'HH:mm:ssZZ");

            return formattedDate;
        },
        toUserFormatFromSearchPatterDate(dateString) {
            const userDateFormat = store.getters['account/user'].MetricDateFormat;

            const inputDate = DateTime.fromFormat(dateString, 'dd.MM.yyyy');
            return inputDate.toFormat(userDateFormat);
        }
    },
    text: {
        replaceAll: function (strValue, strPatter, replacement) {
            return strValue.split(strPatter).join(replacement);
        },

        padLeft: function (str, max, chr) {
            str = str.toString();
            return str.length < max ? this.padLeft(chr + str, max, chr) : str;
        },
        contains: function (prop, value) {
            if (typeof prop === 'string' && typeof value === 'string') { return prop.toLowerCase().indexOf(value.toLowerCase()) >= 0; }

            if (Array.isArray(prop)) {
                let matched = false;

                prop.forEach((el) => {
                    const a = ('' + el).toLowerCase();
                    const val = ('' + value).toLowerCase();

                    matched = matched || (a.indexOf(val) >= 0);
                });

                return matched;
            }
            return false;
        },
        compare(prop, desc = true) {
            var descending = desc ? 1 : -1;
            return function (a, b) {
                var result = (a[prop] < b[prop]) ? -1 : (a[prop] > b[prop]) ? 1 : 0;
                return result * descending;
            };
        },
    },
    number: {
        round: function (value, places) {
            return +(Math.round(value + 'e+' + places) + 'e-' + places);
        },
        truncate: function (value, scale) {
            const factor = Math.pow(10, scale);
            return Math.trunc(value * factor) / factor;
        },
        fromUserFormat: function (strNum, decSep, thouSep) {
            if (strNum) {
                var num = parseFloat(strNum.toString().replaceAll(thouSep, '').replaceAll(decSep, '.'));
                return isNaN(num) ? 0 : num;
            } else {
                return 0;
            }
        },
        toUserFormat: function (num, decSep, thouSep) {
            if (!num) return num;

            const numStr = num.toString();

            let [integerPart, decimalPart] = numStr.split('.');

            integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thouSep);

            const number = decimalPart ? `${integerPart}${decSep}${decimalPart}` : integerPart;
            return number;
        },
        changeFormat: function (strNum, inputDecSep, inputThouSep, outputDecSep, outputThouSep) {
            if (strNum) {
                const num = this.fromUserFormat(strNum, inputDecSep, inputThouSep);
                return this.toUserFormat(num, outputDecSep, outputThouSep);
            } else {
                return strNum;
            }
        },
        toUserFormatFromSearchPatternNumber(number) {
            const userDecSep = store.getters['account/user'].MetricDecimalSep;
            const userThouSep = store.getters['account/user'].MetricThousandSep;

            const numberWithThousandSeparator = new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 10,
                useGrouping: true
            }).format(number);
            number = numberWithThousandSeparator.replace(',', userThouSep).replace('.', userDecSep);
            return number;
        }
    },
    file: {
        attachment: {
            async getAsync(route) {
                const accessToken = store.getters['account/token'];
                const baseUrl = `${store.getters['account/apiUrl']}/api`;
                const url = `${baseUrl}/${route}`;

                const response = await fetch(url, {
                    headers: { Authorization: `Bearer ${accessToken}` }
                });
                const blob = await response.blob();
                const objectUrl = URL.createObjectURL(blob);
                setTimeout(() => {
                    URL.revokeObjectURL(objectUrl);
                }, 5000);
                return objectUrl;
            },
            get(route) {
                const accessToken = store.getters['account/token'];
                const baseUrl = `${store.getters['account/apiUrl']}/api`;
                const url = `${baseUrl}/${route}`;
                return fetch(url, {
                    headers: { Authorization: `Bearer ${accessToken}` }
                }).then(response => response.blob())
                .then(blob => {
                    const objectUrl = URL.createObjectURL(blob);
                    setTimeout(() => {
                        URL.revokeObjectURL(objectUrl);
                    }, 5000);
                    return objectUrl;
                });
            },
            async download(route, options = {}) {
                const accessToken = store.getters['account/token'];
                const baseUrl = `${store.getters['account/apiUrl']}/api`;
                const url = `${baseUrl}/${route}`;
                const fetchOptions = {
                    ...options,
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        ...options.headers
                    }
                };
                const response = await fetch(url, fetchOptions);
                if (!response.ok) {
                    var error = await response.json();
                    window.getApp.$emit('APP_ERROR', error);
                } else {
                    const filename = decodeURI(response.headers.get('content-disposition')
                        .split(';')
                        .find(n => n.includes('filename='))
                        .replace('filename=', '')
                        .trim());

                    const blob = await response.blob();

                    saveAs(blob, filename);
                }
            },
            print(route) {
                const accessToken = store.getters['account/token'];
                const baseUrl = `${store.getters['account/apiUrl']}/api`;

                fetch(`${baseUrl}/${route}`, {
                    headers: {
                        Authorization: `Bearer ${accessToken}`
                    }
                })
                .then(response => {
                    return response.blob();
                })
                .then(blob => {
                    const blobUrl = URL.createObjectURL(blob);
                    printjs(blobUrl);

                    setTimeout(() => {
                        URL.revokeObjectURL(blobUrl);
                    }, 2000);
                })
                .catch(error => {
                    console.error('Error printing:', error);
                });
            },
        },
        image: {
            getBase64: base64Data => {
                if (base64Data) {
                    const data = base64Data.split(';');
                    return {
                        extension: data[0].replaceAll('data:image/', ''),
                        data: data[1].replaceAll('base64,', ''),
                    };
                }
                return null;
            },
            toBase64: base64Data => {
                if (base64Data) {
                    return `data:image/jpeg;base64,${base64Data}`;
                }
                return null;
            },
            generateAvatar(letters, sizeParam) {
                var canvas = document.createElement('canvas');
                var context = canvas.getContext('2d');
                var size = sizeParam || 60;

                // Generate a random color every time function is called
                var color = '#' + (Math.random() * 0xFFFFFF << 0).toString(16);

                // Set canvas with & height
                canvas.width = size;
                canvas.height = size;

                // Select a font family to support different language characters
                // like Arial
                context.font = Math.round(canvas.width / 2) + 'px Arial';
                context.textAlign = 'center';

                // Setup background and front color
                context.fillStyle = color;
                context.fillRect(0, 0, canvas.width, canvas.height);
                context.fillStyle = '#FFF';
                context.fillText(letters, size / 2, size / 1.5);

                // Set image representation in default format (png)
                const dataURI = canvas.toDataURL();

                // Dispose canvas element
                canvas = null;

                return dataURI;
            }
        },
        toBase64: file => {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => resolve(reader.result);
                reader.onerror = error => reject(error);
            });
        },
        bytesToSize(bytes) {
            var sizes = ['bytes', 'kb', 'mb', 'gb', 'tb'];
            if (bytes == 0) return '0 Byte';
            var i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
            return Math.round(bytes / Math.pow(1024, i), 2) + ' ' + sizes[i];
        },
    },
    grid: {
        getParameters(url, options) {
            options = options || {};

            const baseUrl = `${store.getters['account/apiUrl']}/api/`;

            return createStore(
                {
                    key: options.id || 'Id',
                    loadMethod: options.method || 'POST',
                    loadUrl: baseUrl + url,
                    onBeforeSend: (method, ajaxOptions) => {
                        ajaxOptions.headers = {
                            // "Content-Type": "application/json",
                            Authorization: 'bearer ' + store.getters['account/token'],
                        };
                    },
                    onAjaxError: function (e) {
                        if (e.xhr.status === 403 || e.xhr.status === 401) {
                            window.getApp.$emit('APP_ACCESS_DENIED', e.error);
                        }
                    }
                }
            );
        },
    },
    array: {
        compare(prop, desc = true) {
            var descending = desc ? 1 : -1;
            return function (a, b) {
                var result = (a[prop] < b[prop]) ? -1 : (a[prop] > b[prop]) ? 1 : 0;
                return result * descending;
            };
        },
        async mapWithConcurrency(list, limit, worker) {
            const executing = new Set();

            for (const item of list) {
                const p = (async () => worker(item))();

                executing.add(p);
                p.finally(() => executing.delete(p));

                if (executing.size >= limit) {
                    await Promise.race(executing);
                }
            }

            await Promise.all(executing);
        }
    },
    mix: {
        randomColor: function () {
            return '#' + (Math.random() * 0xFFFFFF << 0).toString(16);
        },
        getLanguage() {
            var languageCode = store.getters['account/user'] ? store.getters['account/user'].CultureCode : '';
            if (!languageCode) {
                var defaultLanguage = store.getters['account/defaultLanguage'] || 'auto';
                if (defaultLanguage == 'auto') {
                    languageCode = (navigator.languages
                        ? navigator.languages[0]
                        : navigator.language || navigator.userLanguage
                    ).substring(0, 2);
                }
            }
            switch (languageCode) {
                case 'de':
                    return 1;
                case 'en':
                    return 2;
                case 'es':
                    return 3;
                case 'fr':
                    return 4;
                case 'it':
                    return 5;
                case 'ro':
                    return 6;
                case 'pt':
                    return 7;
            }
            return 1;
        },
        getContentTypeBaseName(name) {
            const trimmed = name.trim();
            const lastSpaceIndex = trimmed.lastIndexOf(' ');
            if (lastSpaceIndex === -1) return trimmed;

            const suffix = trimmed.slice(lastSpaceIndex + 1);
            if (/^v\d+(\.\d+)?$/i.test(suffix)) {
                return trimmed.slice(0, lastSpaceIndex).trim();
            }
            return trimmed;
        }
    },
    validators: {
        numericFieldMaxValue: value => {
            if (typeof value !== 'number') {
                const parts = value.split('.');
                const intPart = parts[0];
                const decimalPart = parts[1];
                // Split string to check if it has decimals since if we use Number() it rounds decimals.
                if (intPart.length === NUMERIC_MAX_VALUE.toString().length) {
                    if (decimalPart) {
                        // Check if all decimals are 0
                        if (/^0+$/.test(decimalPart)) {
                            return Number(value) <= NUMERIC_MAX_VALUE;
                        }
                        return false;
                    }
                }

                return Number(value) <= NUMERIC_MAX_VALUE;
            }
            return value <= NUMERIC_MAX_VALUE;
        },
        customEmail: value => {
            if (!value) return true;
            const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return regex.test(value);
        }
    }
};
